/** Experimental Studio-observed agent-wide Monitor aggregates, not own-user activity. */
import { z } from "zod";
import { randomUUID } from "node:crypto";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import type { DataverseApi } from "./dataverse-api.js";
import { ACTIVITY_ENVIRONMENT_PATTERN, ACTIVITY_GUID_PATTERN } from "./copilot-activity-api.js";
import { environmentIdToApiHost } from "./connections-api.js";
import { requestWithRetry } from "../utils/http.js";
import { ApiError } from "../utils/errors.js";

const DEADLINE_MS = 15000;
const MAX_BYTES = 1024 * 1024;
const MAX_ROWS = 200;
// eslint-disable-next-line no-control-regex
const SESSION_CONTROL_PATTERN = /[\u0000-\u001f\u007f]/;
export const MONITOR_UTC_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$(?![\s\S])/;
function canonicalUtc(value: string): boolean {
  if (!MONITOR_UTC_PATTERN.test(value)) return false;
  const parsed = Date.parse(value);
  return (
    Number.isFinite(parsed) && parsed > -62135596800001 && new Date(parsed).toISOString() === value
  );
}
export const getCopilotAgentSessionSummaryInputSchema = z
  .object({
    environment: z.string().regex(ACTIVITY_ENVIRONMENT_PATTERN),
    agentId: z.string().regex(ACTIVITY_GUID_PATTERN),
    utcStartTime: z
      .string()
      .length(24)
      .refine(canonicalUtc, "Use a valid canonical UTC timestamp with milliseconds."),
    utcEndTime: z
      .string()
      .length(24)
      .refine(canonicalUtc, "Use a valid canonical UTC timestamp with milliseconds."),
  })
  .strict()
  .superRefine((value, context) => {
    const duration = Date.parse(value.utcEndTime) - Date.parse(value.utcStartTime);
    if (!Number.isFinite(duration) || duration <= 0 || duration > 31 * 86400000)
      context.addIssue({
        code: "custom",
        path: ["utcEndTime"],
        message: "The UTC interval must be positive and at most 31 days.",
      });
  });
type MonitorInput = z.infer<typeof getCopilotAgentSessionSummaryInputSchema>;
type Reason =
  | "input"
  | "environment"
  | "identity"
  | "agent"
  | "authentication"
  | "http"
  | "response"
  | "deadline"
  | "unavailable";
const REASONS: Record<Reason, string> = {
  input: "invalid exact target or UTC interval",
  environment: "explicit target must match the unchanged current Dataverse environment",
  identity: "the selected account could not be preserved",
  agent: "exact agent identity or creation date preflight unavailable",
  authentication: "existing silent Power Platform authentication unavailable",
  http: "session analytics request was refused",
  response: "session analytics response is malformed or exceeds bounds",
  deadline: "session analytics deadline exceeded",
  unavailable: "required API capability unavailable",
};
// Correlation metadata only: never inspect the body or infer a permission fix.
function correlationSuffix(clientRequestId: unknown, headers: unknown): string {
  const guid = (value: unknown): value is string =>
    typeof value === "string" && value.length === 36 && ACTIVITY_GUID_PATTERN.test(value);
  if (!guid(clientRequestId)) return "";
  const ids: string[] = [];
  if (headers !== null && typeof headers === "object" && !Array.isArray(headers)) {
    const source = headers as Record<string, unknown>;
    for (const key of [
      "request-id",
      "x-ms-request-id",
      "x-ms-correlation-request-id",
      "x-ms-correlation-id",
    ]) {
      const value = source[key];
      if (guid(value) && !ids.includes(value.toLowerCase())) ids.push(value.toLowerCase());
      if (ids.length === 3) break;
    }
  }
  return (
    ` Client request ID: ${clientRequestId.toLowerCase()}.` +
    (ids.length
      ? ` Response request/correlation IDs: ${ids.join(", ")}.`
      : " Response request/correlation IDs unavailable.")
  );
}
export class CopilotMonitorError extends ApiError {
  constructor(
    reason: Reason,
    statusCode?: number,
    correlation?: { clientRequestId: unknown; headers: unknown },
  ) {
    const status =
      Number.isInteger(statusCode) && statusCode! >= 100 && statusCode! <= 599 ? statusCode! : 502;
    super(
      `Copilot Monitor session summary: ${REASONS[reason]}${reason === "http" ? ` (HTTP ${status})` : ""}. No session details returned; no retry, fallback, sign-in or tenant change was attempted.` +
        (reason === "http" && correlation
          ? correlationSuffix(correlation.clientRequestId, correlation.headers)
          : ""),
      status,
    );
    this.name = "CopilotMonitorError";
  }
}
function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function canonicalEnvironment(value: string) {
  return /^default-/i.test(value) ? `Default-${value.slice(8).toLowerCase()}` : value.toLowerCase();
}
function creationDate(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 33) return null;
  const match =
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,7}))?(?:Z|\+00:00)$(?![\s\S])/.exec(value);
  if (!match) return null;
  const canonical = `${match[1]}.${(match[2] ?? "").padEnd(3, "0").slice(0, 3)}Z`;
  return canonicalUtc(canonical) ? canonical : null;
}
const CHANNELS = ["pva-published-engine-direct", "directline"] as const;
const OUTCOMES = [
  "None",
  "ResolvedImplied",
  "Abandoned",
  "AgentTransferFromQuestionMaxAttempts",
] as const;
const ORCHESTRATIONS = ["Generative", "Classic"] as const;
function buckets(labels: readonly string[]) {
  return {
    known: Object.fromEntries(labels.map((label) => [label, 0])) as Record<string, number>,
    unrecognized: 0,
    missing: 0,
    null: 0,
    invalid: 0,
  };
}
function addBucket(target: ReturnType<typeof buckets>, value: unknown) {
  if (value === undefined) target.missing++;
  else if (value === null) target.null++;
  else if (typeof value !== "string" || value.length === 0 || value.length > 128) target.invalid++;
  else if (Object.hasOwn(target.known, value)) target.known[value] = target.known[value]! + 1;
  else target.unrecognized++;
}

// JSON.parse can round decimal/exponent lexemes into safe integers. Inspect only
// the root totalItems token, not unrelated numeric duration/metadata fields.
function exactTotalInteger(text: string): boolean {
  let depth = 0,
    matches = 0,
    valid = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      const start = i++;
      for (; i < text.length && text[i] !== '"'; i++) if (text[i] === "\\") i++;
      let next = i + 1;
      while (next < text.length && /\s/.test(text[next]!)) next++;
      if (
        depth !== 1 ||
        text[next] !== ":" ||
        JSON.parse(text.slice(start, i + 1)) !== "totalItems"
      )
        continue;
      matches++;
      next++;
      const valueStart = next;
      while (next < text.length && text[next] !== "," && text[next] !== "}") next++;
      const value = text.slice(valueStart, next).trim();
      valid =
        /^(?:0|[1-9][0-9]{0,15})$(?![\s\S])/.test(value) &&
        (value.length < 16 || value <= "9007199254740991");
    } else if (char === "{" || char === "[") depth++;
    else if (char === "}" || char === "]") depth--;
  }
  return matches === 1 && valid;
}

/** One first response only; the observed endpoint is experimental, not a public quota/runtime contract. */
export class CopilotMonitorApi {
  constructor(
    private readonly authProvider: AuthProvider,
    private readonly dataverseApi: Pick<DataverseApi, "getCopilotAgentIdentitySilent">,
    private readonly currentEnvironmentId: () => string,
  ) {}
  async getSessionSummary(input: MonitorInput) {
    const parsed = getCopilotAgentSessionSummaryInputSchema.safeParse(input);
    if (!parsed.success) throw new CopilotMonitorError("input");
    const { utcStartTime, utcEndTime } = parsed.data;
    const environment = canonicalEnvironment(parsed.data.environment);
    const agentId = parsed.data.agentId.toLowerCase();
    const controller = new AbortController();
    let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const destroy = () => {
      try {
        body?.destroy();
      } catch {
        /* Never expose cleanup errors. */
      }
    };
    const active = () => {
      if (controller.signal.aborted) {
        destroy();
        throw new CopilotMonitorError("deadline");
      }
      const current = this.currentEnvironmentId();
      if (
        typeof current !== "string" ||
        !ACTIVITY_ENVIRONMENT_PATTERN.test(current) ||
        canonicalEnvironment(current) !== environment
      )
        throw new CopilotMonitorError("environment");
    };
    const work = async () => {
      active();
      if (
        !this.authProvider.tryGetTokenForResourceSilent ||
        !this.dataverseApi.getCopilotAgentIdentitySilent
      )
        throw new CopilotMonitorError("unavailable");
      const getSelected = () => {
        try {
          return this.authProvider.getCurrentAccount();
        } catch {
          throw new CopilotMonitorError("identity");
        }
      };
      const before = getSelected();
      if (
        !before ||
        typeof before.username !== "string" ||
        !before.username.trim() ||
        typeof before.tenantId !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(before.tenantId) ||
        typeof before.localAccountId !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(before.localAccountId)
      )
        throw new CopilotMonitorError("identity");
      const selected = {
        username: before.username,
        tenantId: before.tenantId,
        localAccountId: before.localAccountId,
      };
      const check = () => {
        active();
        const now = getSelected();
        if (
          !now ||
          now.username !== selected.username ||
          now.tenantId !== selected.tenantId ||
          now.localAccountId !== selected.localAccountId
        )
          throw new CopilotMonitorError("identity");
      };
      let bot: unknown;
      try {
        bot = await this.dataverseApi.getCopilotAgentIdentitySilent(agentId, {
          signal: controller.signal,
          includeCreatedOn: true,
        });
      } catch {
        throw new CopilotMonitorError("agent");
      }
      check();
      if (
        !object(bot) ||
        typeof bot.botid !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(bot.botid) ||
        bot.botid.toLowerCase() !== agentId
      )
        throw new CopilotMonitorError("agent");
      const botCreateDate = creationDate(bot.createdon);
      if (!botCreateDate) throw new CopilotMonitorError("agent");
      let token: unknown;
      try {
        token = await this.authProvider.tryGetTokenForResourceSilent(RESOURCES.POWER_PLATFORM_API);
      } catch {
        throw new CopilotMonitorError("authentication");
      }
      check();
      if (typeof token !== "string" || !token.trim() || token.length > 65536 || /\s/.test(token))
        throw new CopilotMonitorError("authentication");
      const query = new URLSearchParams({
        "api-version": "1",
        utcStartTime,
        utcEndTime,
        botCreateDate,
        enableTieredDataRead: "true",
      });
      const clientRequestId = randomUUID();
      const response = await requestWithRetry(
        `https://${environmentIdToApiHost(environment)}/powervirtualagents/bots/${agentId}/analyticsapi/exploration/sessions?${query.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "x-ms-client-request-id": clientRequestId,
          },
          signal: controller.signal,
          headersTimeout: DEADLINE_MS,
          bodyTimeout: DEADLINE_MS,
        },
        { maxAttempts: 1 },
      );
      body = response.body;
      check();
      if (response.statusCode !== 200)
        throw new CopilotMonitorError("http", response.statusCode, {
          clientRequestId,
          headers: response.headers,
        });
      const mime = response.headers["content-type"];
      if (
        typeof mime !== "string" ||
        mime.length > 512 ||
        !/^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i.test(mime)
      )
        throw new CopilotMonitorError("response");
      const chunks: Buffer[] = [];
      let bytes = 0;
      for await (const chunk of body as AsyncIterable<Uint8Array>) {
        check();
        const value = Buffer.from(chunk);
        bytes += value.length;
        if (bytes > MAX_BYTES) throw new CopilotMonitorError("response");
        chunks.push(value);
      }
      check();
      const text = Buffer.concat(chunks).toString("utf8");
      const data: unknown = JSON.parse(text);
      if (!object(data) || !Array.isArray(data.sessions) || data.sessions.length > MAX_ROWS)
        throw new CopilotMonitorError("response");
      const channelCounts = buckets(CHANNELS),
        outcomeCounts = buckets(OUTCOMES),
        orchestrationCounts = buckets(ORCHESTRATIONS);
      const identifiers = new Set<string>();
      for (const row of data.sessions) {
        if (
          !object(row) ||
          typeof row.sessionId !== "string" ||
          row.sessionId.trim().length === 0 ||
          row.sessionId.length > 512 ||
          SESSION_CONTROL_PATTERN.test(row.sessionId) ||
          identifiers.has(row.sessionId)
        )
          throw new CopilotMonitorError("response");
        identifiers.add(row.sessionId);
        addBucket(channelCounts, row.channelId);
        addBucket(outcomeCounts, row.outcome);
        addBucket(orchestrationCounts, row.botOrchestrationType);
      }
      const total =
        typeof data.totalItems === "number" &&
        Number.isSafeInteger(data.totalItems) &&
        data.totalItems >= 0 &&
        exactTotalInteger(text)
          ? data.totalItems
          : null;
      const reportedTotalState =
        total !== null
          ? "available"
          : data.totalItems === undefined
            ? "missing"
            : data.totalItems === null
              ? "null"
              : "invalid";
      return {
        experimental: true,
        source: "studio-monitor-agent-wide-session-aggregates",
        environment,
        agentId,
        utcStartTime,
        utcEndTime,
        scope: "agent-wide-not-own-user",
        sourceRowCount: data.sessions.length,
        reportedTotalItems: total,
        reportedTotalState,
        reportedTotalComparison:
          total === null
            ? "unavailable"
            : total > data.sessions.length
              ? "greater-than-returned"
              : total < data.sessions.length
                ? "less-than-returned"
                : "equal-to-returned",
        channelCounts,
        outcomeCounts,
        orchestrationCounts,
        firstResponseOnly: true,
        exhaustive: false,
        maximumAcceptedRows: MAX_ROWS,
        notice:
          "Counts describe only unique sessions in this one returned response. Closed labels are observed service codes, not independently verified resolutions, ticket actions or runtime success. Agent-wide analytics is not filtered to the current user. Reported totalItems is service metadata, not a verified exhaustive count. Unknown labels are counted without being disclosed. No session/conversation IDs, text, outcome reasons, row timestamps, user identities, metadata payloads or continuation values are returned. Row startTime has no established UTC contract and is not interpreted. No pagination, execution, publication or tenant changes were performed.",
      };
    };
    try {
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            destroy();
            reject(new CopilotMonitorError("deadline"));
          }, DEADLINE_MS);
        }),
      ]);
    } catch (error) {
      if (error instanceof CopilotMonitorError) throw error;
      throw new CopilotMonitorError("response");
    } finally {
      clearTimeout(timer);
      controller.abort();
      destroy();
    }
  }
}
